/**
 * Supabase access for document comment threads. Pure data — no React, no
 * caches. `queries.ts` / `mutations.ts` wrap these in react-query.
 *
 * Two kinds of thread share one shape here:
 *   - an ISSUE-BACKED thread (`linked_issue_id` set) keeps its comments in
 *     `pm_issue_comments` on its sub-issue — the same rows the Issues page
 *     shows — and "complete" is that issue's status;
 *   - an OPTED-OUT thread keeps its comments in `document_comments`.
 * `listThreads` hydrates both into `ThreadRow.comments` and tags the source,
 * and every write takes a target naming which table it belongs to.
 *
 * Every column list is explicit. Names a user sees are resolved through the
 * NAMED foreign keys so PostgREST can embed them in the same round trip
 * (`pm_issue_comments` has no such FK, so its authors are batched instead).
 *
 * The server owns identity and attribution: `created_by`, `building_id` and
 * the `resolved_at` / `resolved_by` pair are stamped by triggers.
 */

import { supabase } from "@/integrations/supabase/client";
import { isMissingFunctionError } from "@/utils/postgrestErrors";
import type {
  AuthorProfileRow,
  CommentRow,
  CommentSource,
  CountsRow,
  DocumentCommentScope,
  DocumentVersionRef,
  MentionAudienceUser,
  ThreadActivityRow,
  ThreadLookupRow,
  ThreadRow,
} from "./types";
import { CommentsUnavailableError } from "./types";

interface PostgrestFailure {
  code?: string | null;
  message: string;
}

/** PostgREST answers PGRST205 for a table missing from its schema cache and
 *  Postgres 42P01 for an undefined table. */
const MISSING_TABLE_CODES = new Set(["PGRST205", "42P01"]);
function fail(error: PostgrestFailure): never {
  if ((error.code && MISSING_TABLE_CODES.has(error.code)) || isMissingFunctionError(error)) {
    throw new CommentsUnavailableError();
  }
  throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Column lists
// ---------------------------------------------------------------------------

export const COMMENT_COLUMNS =
  "id, thread_id, body, is_root, author_id, created_at, modified_at, deleted_at, " +
  "author:user_profiles!document_comments_author_id_fk(id, first_name, last_name, email, avatar_url, color), " +
  "mentions:document_comment_mentions(mentioned_user_id, read_at)";

export const THREAD_COLUMNS =
  "id, document_id, building_id, project_id, page_number, resolved_at, resolved_by, linked_issue_id, " +
  "created_at, created_by, modified_at, deleted_at, " +
  "resolved_by_profile:user_profiles!document_comment_threads_resolved_by_fk(first_name, last_name), " +
  "created_by_profile:user_profiles!document_comment_threads_created_by_fk(first_name, last_name), " +
  "linked_issue:pm_issues!document_comment_threads_linked_issue_fk(id, issue_number, title, status, building_project_id, deleted_at), " +
  "document:documents!document_comment_threads_document_fk(id, version, filename), " +
  `comments:document_comments(${COMMENT_COLUMNS})`;

export const PM_COMMENT_COLUMNS =
  "id, issue_id, author_id, body, created_at, modified_at, deleted_at";
const AUTHOR_COLUMNS = "id, first_name, last_name, email, avatar_url, color";

const SCOPE_COLUMNS =
  "id, version, version_group, project_id, building_id, org_id, filename, is_latest_version, is_deleted";

const ACTIVITY_COLUMNS = "id, thread_id, action_type, actor_id, metadata, created_at";

const LOOKUP_COLUMNS = "id, document_id, building_id, page_number, resolved_at, deleted_at";

const COMMENT_TABLE: Record<CommentSource, string> = {
  document: "document_comments",
  pm: "pm_issue_comments",
};
const MENTION_TABLE: Record<CommentSource, string> = {
  document: "document_comment_mentions",
  pm: "pm_issue_comment_mentions",
};

// ---------------------------------------------------------------------------
// Scope + lineage
// ---------------------------------------------------------------------------

interface ScopeRow {
  id: number;
  version: number | null;
  version_group: string | null;
  project_id: number | null;
  building_id: number | null;
  org_id: string;
  filename: string;
  is_latest_version: boolean | null;
  is_deleted: boolean | null;
}

/** The fields a comments surface needs about the document it opened on. */
export async function fetchThreadScope(documentId: number): Promise<DocumentCommentScope | null> {
  const { data, error } = await supabase
    .from("documents")
    .select(SCOPE_COLUMNS)
    .eq("id", documentId)
    .maybeSingle();
  if (error) fail(error);
  const row = data as ScopeRow | null;
  if (!row || row.building_id == null) return null;
  return {
    id: row.id,
    version: row.version,
    versionGroup: row.version_group,
    projectId: row.project_id,
    buildingId: row.building_id,
    orgId: row.org_id,
    filename: row.filename,
    isLatestVersion: row.is_latest_version,
    isDeleted: Boolean(row.is_deleted),
  };
}

interface VersionRow {
  id: number;
  version: number | null;
  filename: string;
  is_deleted: boolean | null;
}

/**
 * Every live version in the scope's lineage, newest first, always including
 * the scope document itself (even from the trash, so its own threads show).
 */
export async function fetchVersionGroupDocuments(
  scope: DocumentCommentScope
): Promise<DocumentVersionRef[]> {
  const self: DocumentVersionRef = {
    id: scope.id,
    version: scope.version,
    filename: scope.filename,
    isDeleted: scope.isDeleted,
  };
  if (!scope.versionGroup) return [self];

  const { data, error } = await supabase
    .from("documents")
    .select("id, version, filename, is_deleted")
    .eq("version_group", scope.versionGroup)
    .or(`is_deleted.eq.false,id.eq.${scope.id}`)
    .order("version", { ascending: false });
  if (error) fail(error);

  const rows = ((data ?? []) as VersionRow[]).map((r) => ({
    id: r.id,
    version: r.version,
    filename: r.filename,
    isDeleted: Boolean(r.is_deleted),
  }));
  return rows.some((r) => r.id === scope.id) ? rows : [self, ...rows];
}

// ---------------------------------------------------------------------------
// Threads
// ---------------------------------------------------------------------------

/** Live threads on the given documents, oldest first, with their comments. */
export async function listThreads(documentIds: number[]): Promise<ThreadRow[]> {
  if (documentIds.length === 0) return [];
  const { data, error } = await supabase
    .from("document_comment_threads")
    .select(THREAD_COLUMNS)
    .in("document_id", documentIds)
    .is("deleted_at", null)
    .order("created_at", { ascending: true });
  if (error) fail(error);
  const rows = (data ?? []) as ThreadRow[];
  const issueIds = rows.map((r) => r.linked_issue_id).filter((id): id is number => id != null);
  const pmByIssue = await fetchPmCommentsByIssue(issueIds);
  return rows.map((row) => {
    if (row.linked_issue_id != null) {
      return {
        ...row,
        comment_source: "pm" as const,
        comments: (pmByIssue.get(row.linked_issue_id) ?? []).map((c) => ({
          ...c,
          thread_id: row.id,
        })),
      };
    }
    return {
      ...row,
      comment_source: "document" as const,
      comments: (row.comments ?? [])
        .filter((c) => c.deleted_at === null)
        .sort((a, b) => a.created_at.localeCompare(b.created_at)),
    };
  });
}

interface PmCommentRow {
  id: number;
  issue_id: number;
  author_id: number;
  body: string;
  created_at: string;
  modified_at: string | null;
  deleted_at: string | null;
}

/**
 * The sub-issues' comments, converted to the thread comment shape: the
 * earliest comment on an issue is the thread's root. Three batched reads —
 * comments, their mentions, their authors — whatever the thread count.
 */
async function fetchPmCommentsByIssue(issueIds: number[]): Promise<Map<number, CommentRow[]>> {
  const out = new Map<number, CommentRow[]>();
  const ids = Array.from(new Set(issueIds));
  if (ids.length === 0) return out;

  const { data, error } = await supabase
    .from("pm_issue_comments")
    .select(PM_COMMENT_COLUMNS)
    .in("issue_id", ids)
    .is("deleted_at", null)
    .order("created_at", { ascending: true });
  if (error) fail(error);
  const comments = (data ?? []) as PmCommentRow[];
  if (comments.length === 0) return out;

  const commentIds = comments.map((c) => c.id);
  const authorIds = Array.from(new Set(comments.map((c) => c.author_id)));
  const [mentionsResult, authorsResult] = await Promise.all([
    supabase
      .from("pm_issue_comment_mentions")
      .select("comment_id, mentioned_user_id, read_at")
      .in("comment_id", commentIds),
    supabase.from("user_profiles").select(AUTHOR_COLUMNS).in("id", authorIds),
  ]);
  if (mentionsResult.error) fail(mentionsResult.error);
  if (authorsResult.error) fail(authorsResult.error);

  const mentions = new Map<number, Array<{ mentioned_user_id: number; read_at: string | null }>>();
  for (const m of (mentionsResult.data ?? []) as Array<{
    comment_id: number;
    mentioned_user_id: number;
    read_at: string | null;
  }>) {
    const list = mentions.get(m.comment_id) ?? [];
    list.push({ mentioned_user_id: m.mentioned_user_id, read_at: m.read_at });
    mentions.set(m.comment_id, list);
  }
  const authors = new Map<number, AuthorProfileRow>(
    ((authorsResult.data ?? []) as AuthorProfileRow[]).map((a) => [a.id, a])
  );

  for (const c of comments) {
    const list = out.get(c.issue_id) ?? [];
    list.push({
      id: c.id,
      thread_id: 0,
      body: c.body,
      is_root: list.length === 0,
      author_id: c.author_id,
      created_at: c.created_at,
      modified_at: c.modified_at,
      deleted_at: c.deleted_at,
      author: authors.get(c.author_id) ?? null,
      mentions: mentions.get(c.id) ?? [],
    });
    out.set(c.issue_id, list);
  }
  return out;
}

export interface CreateThreadInput {
  documentId: number;
  pageNumber: number | null;
  body: string;
  mentionedUserIds: number[];
  /** File the thread as a sub-issue of the document's issue (the default). */
  createIssue: boolean;
}

export interface CreateThreadResult {
  threadId: number;
  issueId: number | null;
  parentIssueId: number | null;
}

/** One RPC: the thread, its root comment (in the issue or the document
 *  table) and the mentions, atomically. */
export async function createThread(input: CreateThreadInput): Promise<CreateThreadResult> {
  const { data, error } = await supabase.rpc("document_thread_create", {
    p_document_id: input.documentId,
    p_page_number: input.pageNumber,
    p_body: input.body,
    p_mentioned_user_ids: Array.from(new Set(input.mentionedUserIds)),
    p_create_issue: input.createIssue,
  });
  if (error) fail(error);
  const row = (data ?? {}) as {
    thread_id?: number;
    issue_id?: number | null;
    parent_issue_id?: number | null;
  };
  if (row.thread_id == null) throw new Error("document_thread_create returned no thread");
  return {
    threadId: row.thread_id,
    issueId: row.issue_id ?? null,
    parentIssueId: row.parent_issue_id ?? null,
  };
}

/** File an opted-out thread as a sub-issue later; its comments move across. */
export async function promoteThreadToIssue(threadId: number): Promise<number> {
  const { data, error } = await supabase.rpc("document_thread_create_issue", {
    p_thread_id: threadId,
  });
  if (error) fail(error);
  const row = (data ?? {}) as { issue_id?: number };
  if (row.issue_id == null) throw new Error("document_thread_create_issue returned no issue");
  return row.issue_id;
}

/** Which table a comment write belongs to. */
export interface CommentTarget {
  threadId: number;
  /** The sub-issue for an issue-backed thread; null for an opted-out one. */
  issueId: number | null;
  /** The writer — `pm_issue_comments.author_id` has no default. */
  authorId: number;
}

export async function addReply(
  target: CommentTarget,
  body: string,
  mentionedUserIds: number[]
): Promise<number> {
  const unique = Array.from(new Set(mentionedUserIds));
  let commentId: number;
  let source: CommentSource;
  if (target.issueId != null) {
    const { data, error } = await supabase
      .from("pm_issue_comments")
      .insert({ issue_id: target.issueId, author_id: target.authorId, body })
      .select("id")
      .single();
    if (error) fail(error);
    commentId = (data as { id: number }).id;
    source = "pm";
  } else {
    const { data, error } = await supabase
      .from("document_comments")
      .insert({ thread_id: target.threadId, body, is_root: false })
      .select("id")
      .single();
    if (error) fail(error);
    commentId = (data as { id: number }).id;
    source = "document";
  }
  if (unique.length > 0) {
    const { error } = await supabase
      .from(MENTION_TABLE[source])
      .insert(unique.map((userId) => ({ comment_id: commentId, mentioned_user_id: userId })));
    if (error) fail(error);
  }
  return commentId;
}

/**
 * Edit a comment body and reconcile its mentions to the new text: rows for
 * users no longer mentioned are removed, new ones added, and untouched ones
 * keep their `read_at`.
 */
export async function editComment(
  commentId: number,
  source: CommentSource,
  body: string,
  mentionedUserIds: number[],
  modifiedBy: number
): Promise<void> {
  const payload =
    source === "pm"
      ? { body, modified_at: new Date().toISOString(), modified_by: modifiedBy }
      : { body };
  const { error } = await supabase.from(COMMENT_TABLE[source]).update(payload).eq("id", commentId);
  if (error) fail(error);

  const { data: existing, error: readErr } = await supabase
    .from(MENTION_TABLE[source])
    .select("mentioned_user_id")
    .eq("comment_id", commentId);
  if (readErr) fail(readErr);

  const wanted = new Set(mentionedUserIds);
  const current = new Set(
    ((existing ?? []) as Array<{ mentioned_user_id: number }>).map((m) => m.mentioned_user_id)
  );
  const toRemove = [...current].filter((id) => !wanted.has(id));
  const toAdd = [...wanted].filter((id) => !current.has(id));

  if (toRemove.length > 0) {
    const { error: delErr } = await supabase
      .from(MENTION_TABLE[source])
      .delete()
      .eq("comment_id", commentId)
      .in("mentioned_user_id", toRemove);
    if (delErr) fail(delErr);
  }
  if (toAdd.length > 0) {
    const { error: insErr } = await supabase
      .from(MENTION_TABLE[source])
      .insert(toAdd.map((userId) => ({ comment_id: commentId, mentioned_user_id: userId })));
    if (insErr) fail(insErr);
  }
}

/** Soft delete of one reply. */
export async function deleteComment(commentId: number, source: CommentSource): Promise<void> {
  const { error } = await supabase
    .from(COMMENT_TABLE[source])
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", commentId);
  if (error) fail(error);
}

/** Soft-delete a whole thread; an issue-backed thread's sub-issue follows by trigger. */
export async function deleteThread(threadId: number): Promise<void> {
  const { error } = await supabase
    .from("document_comment_threads")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", threadId);
  if (error) fail(error);
}

/**
 * Complete / reopen. An issue-backed thread IS its issue's status, so the
 * write goes to `pm_issues` and the mirror trigger stamps the thread; for an
 * opted-out thread the value sent is only a signal — the trigger stamps the
 * real time and completer.
 */
export async function setThreadResolved(
  thread: { id: number; issueId: number | null },
  resolved: boolean
): Promise<void> {
  if (thread.issueId != null) {
    const { error } = await supabase
      .from("pm_issues")
      .update({ status: resolved ? "resolved" : "in_progress" })
      .eq("id", thread.issueId);
    if (error) fail(error);
    return;
  }
  const { error } = await supabase
    .from("document_comment_threads")
    .update({ resolved_at: resolved ? new Date().toISOString() : null })
    .eq("id", thread.id);
  if (error) fail(error);
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

/** One thread by id (deep links). Null when hidden by RLS or missing. */
export async function lookupThread(threadId: number): Promise<ThreadLookupRow | null> {
  const { data, error } = await supabase
    .from("document_comment_threads")
    .select(LOOKUP_COLUMNS)
    .eq("id", threadId)
    .maybeSingle();
  if (error) fail(error);
  return (data as ThreadLookupRow | null) ?? null;
}

/** The thread an issue was filed from, if any. */
export async function lookupThreadForIssue(issueId: number): Promise<ThreadLookupRow | null> {
  const { data, error } = await supabase
    .from("document_comment_threads")
    .select(LOOKUP_COLUMNS)
    .eq("linked_issue_id", issueId)
    .is("deleted_at", null)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) {
    if (error.code && MISSING_TABLE_CODES.has(error.code)) return null;
    throw new Error(error.message);
  }
  return (data as ThreadLookupRow | null) ?? null;
}

export async function fetchThreadActivity(threadId: number): Promise<ThreadActivityRow[]> {
  const { data, error } = await supabase
    .from("document_comment_thread_activity")
    .select(ACTIVITY_COLUMNS)
    .eq("thread_id", threadId)
    .order("created_at", { ascending: true });
  if (error) fail(error);
  return (data ?? []) as ThreadActivityRow[];
}

/**
 * Open / completed counts per document id, summed over each document's live
 * version group. Missing schema (the migration has not landed) yields an
 * empty map: badges simply do not render.
 */
export async function fetchCounts(documentIds: number[]): Promise<Map<number, CountsRow>> {
  const result = new Map<number, CountsRow>();
  if (documentIds.length === 0) return result;
  const { data, error } = await supabase.rpc("document_comment_counts", {
    p_document_ids: documentIds,
  });
  if (error) {
    if (isMissingFunctionError(error)) return result;
    fail(error);
  }
  for (const row of (data ?? []) as CountsRow[]) result.set(row.document_id, row);
  return result;
}

/** Who may be @mentioned on a document (project audience, or the building's). */
export async function fetchMentionAudience(documentId: number): Promise<MentionAudienceUser[]> {
  const { data, error } = await supabase.rpc("get_document_assignable_users", {
    p_document_id: documentId,
  });
  if (error) {
    if (isMissingFunctionError(error)) return [];
    fail(error);
  }
  return (data ?? []) as MentionAudienceUser[];
}

/** Mark the caller's unread mentions on these comments read (RLS scopes the
 *  update to their own rows in either table). */
export async function markMentionsRead(
  items: Array<{ commentId: number; source: CommentSource }>,
  currentUserId: number
): Promise<void> {
  const bySource: Record<CommentSource, number[]> = { document: [], pm: [] };
  for (const item of items) bySource[item.source].push(item.commentId);
  for (const source of ["document", "pm"] as const) {
    const ids = bySource[source];
    if (ids.length === 0) continue;
    const { error } = await supabase
      .from(MENTION_TABLE[source])
      .update({ read_at: new Date().toISOString() })
      .in("comment_id", ids)
      .eq("mentioned_user_id", currentUserId)
      .is("read_at", null);
    if (error) fail(error);
  }
}

/** Best-effort: a missing RPC (pre-migration) reads as zero. */
export async function getUnreadDocumentMentionCount(buildingId: number): Promise<number> {
  const { data, error } = await supabase.rpc("document_comment_unread_mention_count", {
    p_building_id: buildingId,
  });
  if (error) {
    if (isMissingFunctionError(error)) return 0;
    throw new Error(error.message);
  }
  return typeof data === "number" ? data : 0;
}

export type { CommentRow, ThreadRow };
